/**
 * Per-hotel label overrides (admine → Hotel Settings → Labels).
 *
 * Hotel.label_overrides is { 'ns:path.to.key': 'Hotel wording' } for a
 * curated set of keys (backend hotels/label_overrides.py). They are
 * layered over the loaded i18n bundles for EVERY language — the property
 * chose that wording — so every component that renders the key picks it
 * up with no per-screen change.
 *
 * Applied only after the namespace is loaded: the HTTP backend merges a
 * freshly loaded bundle shallowly, which would wipe an override added
 * before it arrived. Re-applied on each language change for the same
 * reason.
 */

const splitKey = (fullKey) => {
  const i = fullKey.indexOf(':');
  return i > 0
    ? { ns: fullKey.slice(0, i), key: fullKey.slice(i + 1) }
    : { ns: 'common', key: fullKey };
};

export const applyLabelOverrides = async (i18n, overrides) => {
  const entries = Object.entries(overrides || {}).filter(
    ([k, v]) => typeof k === 'string' && typeof v === 'string' && v.trim()
  );
  if (entries.length === 0) return 0;

  const namespaces = [...new Set(entries.map(([k]) => splitKey(k).ns))];
  await i18n.loadNamespaces(namespaces);

  // Current language plus the fallback chain, so a key missing from the
  // active language (falling back to en) still shows the override.
  const langs = [...new Set([i18n.language, ...(i18n.languages || []), 'en'].filter(Boolean))];
  entries.forEach(([fullKey, text]) => {
    const { ns, key } = splitKey(fullKey);
    langs.forEach((lng) => {
      i18n.addResource(lng, ns, key, text.trim(), { silent: true });
    });
  });
  // react-i18next does not re-render on store changes by default; a
  // same-language changeLanguage is the supported nudge.
  await i18n.changeLanguage(i18n.language);
  return entries.length;
};

/**
 * Apply now and on every later language switch. Returns an unsubscribe.
 */
export const installLabelOverrides = (i18n, overrides) => {
  let applying = false;
  const run = async () => {
    if (applying) return;
    applying = true;
    try {
      await applyLabelOverrides(i18n, overrides);
    } finally {
      applying = false;
    }
  };
  const onLanguageChanged = () => { run(); };
  run().then(() => i18n.on('languageChanged', onLanguageChanged));
  return () => i18n.off('languageChanged', onLanguageChanged);
};
